import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import moment from 'moment';
import Dropdown from 'components/shared/Dropdown/Dropdown';
import { ExportIcon } from 'components/icons';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { EXPORT_OPTIONS, TOAST_MESSAGES, generateExportableTableData, modifyFilters } from './utils';
import { useSearchDevicesV2Query } from 'apis/cm';
import { selectUserPreferences } from 'redux/selectors';
import { selectUserPreferenceList } from 'redux/UserPreferences';
import { addToastNotification, clearToastNotifications } from 'redux/actions';
import { DEVICES_PAGE_TAB, USER_PREFERENCE_SETS } from 'common/utils/constants';
import { downloadCsvFile, getKeyByValue, toCSV } from 'common/utils/helpers';
import styles from './DeviceExport.module.css';

const DeviceExport = ({ activeTab, filterDictionary }) => {
  const dispatch = useDispatch();
  const userPreferences = useSelector((state) => selectUserPreferences(state));
  const columnsPreferences = selectUserPreferenceList(userPreferences, USER_PREFERENCE_SETS.COLUMNS_SET);
  const selectedColumnsPreference = useMemo(() => {
    const { state } = columnsPreferences?.[0] || {};
    const tabColumns =
      activeTab === DEVICES_PAGE_TAB.pivot
        ? state?.pivotTab
        : activeTab === DEVICES_PAGE_TAB.echo
        ? state?.echoTab
        : state?.allDevicesTab;
    return tabColumns;
  }, [columnsPreferences, activeTab]);

  const activePolygonId = useSelector((state) => state.ui.devicesPage.activePolygonId);
  const polygonList = useSelector((state) => state.ui.devicesPage.polygons);
  const activePolygon = polygonList.find((polygon) => polygon.deviceGroupId === activePolygonId);

  const reformattedFilters = useMemo(() => {
    return modifyFilters(activeTab, filterDictionary, activePolygon);
  }, [activeTab, filterDictionary, activePolygon]);

  const [exportData, setExportData] = useState(null);
  const [exportType, setExportType] = useState(null);
  const [enableSearchDevicesCall, setEnableSearchDevicesCall] = useState(false);

  const searchDevicesQuery = useSearchDevicesV2Query(
    {
      size: 10000,
      coalesceBundles: true,
      filters: {
        ...reformattedFilters
      }
    },
    {
      enabled: enableSearchDevicesCall,
      placeholderData: []
    }
  );

  const { data, isFetching, isFetched, isError } = searchDevicesQuery;

  const showToast = useCallback(
    (description) => {
      dispatch(clearToastNotifications());
      dispatch(
        addToastNotification({
          id: v4(),
          description
        })
      );
    },
    [dispatch]
  );

  const handleExportData = useCallback((data, filename) => {
    const csv = toCSV(data.columns, data.rows, { fallback: '' });
    downloadCsvFile(csv, filename);
  }, []);

  useEffect(() => {
    if (isFetched && data && !isError && exportType && enableSearchDevicesCall) {
      const { bundles = {}, devices = [] } = data;
      const combinedData = [...Object.values(bundles), ...devices];
      const exportableData = generateExportableTableData(
        combinedData,
        activeTab,
        exportType,
        selectedColumnsPreference
      );
      setExportData(exportableData);
    }
  }, [isFetched, data, isError, selectedColumnsPreference, activeTab, exportType, enableSearchDevicesCall]);

  useEffect(() => {
    if (exportData) {
      const date = moment(new Date().toISOString()).format('MMDDYYYY-HHmmss');
      const filename = `${getKeyByValue(DEVICES_PAGE_TAB, activeTab)}-${date}.csv`;
      handleExportData(exportData, filename);
      setExportData(null);
      setExportType(null);
      setEnableSearchDevicesCall(false);
    }
  }, [exportData, activeTab, handleExportData]);

  useEffect(() => {
    if (isFetching) {
      showToast(TOAST_MESSAGES.pending);
    } else if (isFetched && exportData) {
      showToast(TOAST_MESSAGES.success);
    } else if (isFetched && isError) {
      showToast(TOAST_MESSAGES.failed);
    }
  }, [isFetched, isFetching, isError, exportData, showToast]);

  const handleDropdownSelection = (selection) => {
    setExportType(selection);
    setEnableSearchDevicesCall(true);
  };

  return (
    <div className={styles.root}>
      <Dropdown
        className={isFetching ? styles.dropdownDisabled : styles.dropdown}
        hideArrow={true}
        disabled={isFetching}
        selectItem={async (selection) => !isFetching && handleDropdownSelection(selection)}
        data={Object.values(EXPORT_OPTIONS)}
        customClose={null}
        customButtonContent={
          <IconAndText
            img={<ExportIcon className={isFetching ? styles.exportIconDisabled : styles.exportIcon} />}
            text={'Export'}
          />
        }
      />
    </div>
  );
};

export default DeviceExport;
